import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeaders, mapErrorMessage } from '../../lib/libraryUtils';

/**
 * Hook for trash permanent delete actions
 */
export const useTrashActions = ({ fetchTrash, clearTrashSelection }) => {
  const [deletingItems, setDeletingItems] = useState(false);

  // Permanent delete single document
  const permanentDelete = useCallback(async (docId, docTitle) => {
    setDeletingItems(true);
    try {
      await axios.delete(`${API}/library/trash/${docId}/permanent`, getAuthHeaders());
      toast.success(`"${docTitle}" permanent verwijderd`);
      await fetchTrash();
      return true;
    } catch (error) {
      console.error('Error permanent deleting:', error);
      toast.error(mapErrorMessage(error, 'permanent verwijderen'));
      return false;
    } finally {
      setDeletingItems(false);
    }
  }, [fetchTrash]);

  // Permanent delete bulk
  const permanentDeleteBulk = useCallback(async (ids) => {
    if (ids.length === 0) return { success: false };
    
    setDeletingItems(true);
    try {
      const response = await axios.post(
        `${API}/library/trash/permanent-bulk`,
        { ids },
        getAuthHeaders()
      );
      
      const { success_count, failed_count, failed } = response.data;
      
      if (failed_count === 0) {
        toast.success(`${success_count} document${success_count !== 1 ? 'en' : ''} permanent verwijderd`);
      } else if (success_count > 0) {
        toast.warning(`${success_count} verwijderd, ${failed_count} mislukt`);
        failed.slice(0, 3).forEach(f => toast.error(`${f.title}: ${f.reason}`));
      } else {
        toast.error(`Verwijderen mislukt voor alle ${failed_count} documenten`);
      }
      
      await fetchTrash();
      clearTrashSelection?.();
      return { success: true, success_count, failed_count };
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error(mapErrorMessage(error, 'bulk verwijderen'));
      return { success: false };
    } finally {
      setDeletingItems(false);
    }
  }, [fetchTrash, clearTrashSelection]);

  // Empty trash
  const emptyTrash = useCallback(async () => {
    setDeletingItems(true);
    try {
      const response = await axios.post(`${API}/library/trash/empty`, {}, getAuthHeaders());
      
      const { success_count, failed_count } = response.data;
      
      if (failed_count === 0) {
        toast.success(`Prullenbak geleegd: ${success_count} items verwijderd`);
      } else {
        toast.warning(`${success_count} verwijderd, ${failed_count} mislukt`);
      }
      
      await fetchTrash();
      return { success: true, success_count, failed_count };
    } catch (error) {
      console.error('Error emptying trash:', error);
      toast.error(mapErrorMessage(error, 'legen prullenbak'));
      return { success: false };
    } finally {
      setDeletingItems(false);
    }
  }, [fetchTrash]);

  // Retry failed permanent delete
  const retryDelete = useCallback(async (docId, docTitle) => {
    setDeletingItems(true);
    try {
      await axios.post(`${API}/library/trash/${docId}/retry-delete`, {}, getAuthHeaders());
      toast.success(`"${docTitle}" permanent verwijderd`);
      await fetchTrash();
      return true;
    } catch (error) {
      console.error('Error retrying delete:', error);
      toast.error(mapErrorMessage(error, 'opnieuw proberen'));
      return false;
    } finally {
      setDeletingItems(false);
    }
  }, [fetchTrash]);

  return {
    deletingItems,
    permanentDelete,
    permanentDeleteBulk,
    emptyTrash,
    retryDelete,
  };
};

export default useTrashActions;
